import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class QuadbinPolyfill extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.quadbinpolyfill',
        group: 'Spatial Indexes',
        title: 'Quadbin Polyfill',
        description: `
        This generates a table with indices of all Quadbin cells included within a given extent
        for a given resolution.

      The extent is defined by a table containing polygons.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'resolution',
          type: WorkflowComponentParamType.Number,
          title: 'Resolution',
          helper:
            'Square edge length (equator): 6=626km | 12=10km | 18=150m | 24=2m',
          min: 0,
          max: 26,
          default: 12,
          mode: WorkflowComponentParameterMode.Slider
        },
        {
          name: 'aggregate',
          type: WorkflowComponentParamType.Boolean,
          title: 'Aggregate quadbin cells',
          default: false,
          helper:
            'If false, the result will include the original columns from the input source'
        },
        {
          name: 'includegeocolumns',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include geo columns',
          default: false,
          helper: 'If true, the result will include the original geo column(s)',
          showIf: [{ parameter: 'aggregate', value: false }]
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Mode',
          options: ['center', 'intersects', 'contains'],
          default: 'center',
          mode: WorkflowComponentParameterMode.Toggle,
          providers: [
            Provider.Postgres,
            Provider.BigQuery,
            Provider.DatabricksRest
          ]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let source = ''
    let query = ''
    let select = ''

    if (connection.type === Provider.DatabricksRest) {
      source = dedent`
            (
              SELECT /*+ REPARTITION(40) */
                *
              FROM ${connection.quoteName(preparedParams.source as string)}
              WHERE ${preparedParams.geo} IS NOT NULL
            )`
    } else {
      source = dedent`
            (
              SELECT *
              FROM ${connection.quoteName(preparedParams.source as string)}
              WHERE ${preparedParams.geo} IS NOT NULL
            )`
    }
    if (!schemas && !preparedParams.aggregate) {
      return {
        query: '',
        outputs: {}
      }
    }
    let colsToAdd = ''
    const vectorSchema = schemas?.input?.['source']
    if (!vectorSchema && !preparedParams.aggregate) {
      return {
        query: '',
        outputs: {}
      }
    }
    if (!preparedParams.aggregate) {
      let colsFromSchema = vectorSchema as TableSchema
      if (!preparedParams.includegeocolumns) {
        const typesToExclude = [
          ColumnDataType.Geography,
          ColumnDataType.Geometry
        ]
        if (connection.type === Provider.DatabricksRest) {
          typesToExclude.push(ColumnDataType.Binary)
        }
        colsFromSchema = colsFromSchema.filter(
          (c) => !typesToExclude.includes(c.type)
        )
      }
      if (colsFromSchema.length > 0) {
        colsToAdd = colsFromSchema.map((c) => `s.${c.name}`).join(', ') + ','
      }
    }
    if (connection.type === Provider.BigQuery) {
      if (!preparedParams.aggregate) {
        select = dedent`
                  WITH __quadbin AS
                  (
                    SELECT
                      ${colsToAdd}
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.QUADBIN_POLYFILL_MODE(
                        ${preparedParams.geo},
                        ${preparedParams.resolution},
                        '${preparedParams.mode}'
                        ) quadbins
                    FROM
                      ${source} s
                  )
                  SELECT * EXCEPT(quadbins)
                  FROM __quadbin, __quadbin.quadbins AS quadbin
                  `
      } else {
        select = dedent`
                  WITH __quadbin AS
                  (
                    SELECT ${connection.quoteName(
                      envVars.analyticsToolboxDataset as string
                    )}.QUADBIN_POLYFILL_MODE(
                      ${preparedParams.geo},
                      ${preparedParams.resolution},
                      '${preparedParams.mode}'
                      ) quadbins
                    FROM ${source} s
                  )
                  SELECT DISTINCT quadbin
                  FROM __quadbin, __quadbin.quadbins AS quadbin
                  `
      }
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.Snowflake) {
      if (!preparedParams.aggregate) {
        select = dedent`
                  WITH __quadbin AS
                  (
                    SELECT
                      ${colsToAdd}
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.QUADBIN_POLYFILL(
                        ${preparedParams.geo},
                        ${preparedParams.resolution}
                        ) quadbins
                    FROM
                      ${source} s
                  )
                  SELECT * EXCLUDE(quadbins), quadbin_element.value::BIGINT AS quadbin
                  FROM __quadbin, lateral FLATTEN(input => quadbins) quadbin_element
                  `
      } else {
        select = dedent`
                  WITH __quadbin AS
                  (
                    SELECT
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.QUADBIN_POLYFILL(
                        ${preparedParams.geo},
                        ${preparedParams.resolution}
                        ) quadbins
                    FROM
                      ${source} s
                  )
                  SELECT DISTINCT quadbin_element.value::BIGINT AS quadbin
                  FROM __quadbin, lateral FLATTEN(input => quadbins) quadbin_element
                  `
      }
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.DatabricksRest) {
      if (!preparedParams.aggregate) {
        select = dedent`
                  SELECT /*+ REPARTITION(40) */
                    ${colsToAdd}
                    EXPLODE(QUADBIN_POLYFILL(
                      ST_ASTEXT(${connection.processInputGeogColumn(
                        preparedParams.geo as string
                      )}),
                      ${preparedParams.resolution}
                    )) quadbin
                  FROM
                    ${source} s`
      } else {
        select = dedent`
                  SELECT /*+ REPARTITION(40) */
                    DISTINCT EXPLODE(QUADBIN_POLYFILL(
                    ST_ASTEXT(${connection.processInputGeogColumn(
                      preparedParams.geo as string
                    )}),
                      ${preparedParams.resolution}
                    )) quadbin
                  FROM ${source} s`
      }
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.Postgres) {
      if (!preparedParams.aggregate) {
        select = dedent`
                  WITH __quadbin AS
                  (
                    SELECT
                      ${preparedParams.geo},
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.QUADBIN_POLYFILL(
                        ${preparedParams.geo},
                        ${preparedParams.resolution},
                        '${preparedParams.mode}'
                      ) quadbins
                    FROM
                      ${source} s
                  )
                  SELECT
                    ${colsToAdd}
                    (UNNEST(quadbins)) AS quadbin
                  FROM
                    ${source} s,
                    __quadbin b
                  WHERE b.${preparedParams.geo} = s.${preparedParams.geo}
                  `
      } else {
        select = dedent`WITH __quadbin AS
                  (
                    SELECT
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.QUADBIN_POLYFILL(
                        ${preparedParams.geo},
                        ${preparedParams.resolution},
                        '${preparedParams.mode}'
                      ) quadbins
                    FROM
                      ${source} s
                  )
                  SELECT DISTINCT(UNNEST(quadbins)) AS quadbin
                  FROM __quadbin`
      }
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else {
      const temptable = `${outputTablename}_temp`
      let select = ''
      if (!preparedParams.aggregate) {
        select = dedent`SELECT
                    ${colsToAdd}
                    quadbin::BIGINT
                  FROM
                    ${source} s,
                    ${temptable} b,
                    b.quadbins AS quadbin
                  WHERE ST_EQUALS(
                    b.${preparedParams.geo},
                    s.${preparedParams.geo}
                  )`
      } else {
        select = dedent`SELECT DISTINCT quadbin::BIGINT
                  FROM ${temptable} t, t.quadbins AS quadbin`
      }
      const createTable = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
      query = dedent`
        BEGIN
          EXECUTE '
            CREATE TABLE ${temptable} AS
              SELECT
                ${preparedParams.geo},
                ${connection.quoteName(
                  envVars.analyticsToolboxDataset as string
                )}.QUADBIN_POLYFILL(
                  ${preparedParams.geo},
                  ${preparedParams.resolution}
                ) quadbins
              FROM
                ${source} s';
          ${dedent(createTable)}
          DROP TABLE ${temptable};
        END;`
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.2.0'
      case Provider.BigQuery:
        return '1.1.0'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    const vectorSchema = columns.source
    const outputCols = [{ name: 'quadbin', type: ColumnDataType.Number }]
    if (!params.aggregate) {
      let colsFromSchema = vectorSchema as TableSchema
      const typesToExclude = [ColumnDataType.Geography, ColumnDataType.Geometry]
      if (connection.type === Provider.DatabricksRest) {
        typesToExclude.push(ColumnDataType.Binary)
      }
      if (!params.includegeocolumns) {
        colsFromSchema = colsFromSchema.filter(
          (c) => !typesToExclude.includes(c.type)
        )
      }
      outputCols.push(...colsFromSchema)
    }
    return outputCols
  }
}

export { QuadbinPolyfill }
